import axios from 'axios';
import * as Sentry from '@sentry/react';

const REPORT_THROTTLE_MS = 60 * 1000;

const lastReportedAt = new Map();

const normalizeUrl = (url = '') => {
  const [path] = url.split('?');
  return path.replace(/\/\d+(?=\/|$)/g, '/:id');
};

const isNetworkError = (error) =>
  !axios.isCancel(error) && !error.response && (!!error.request || error.code === 'ECONNABORTED');

const shouldReport = (key) => {
  const now = Date.now();
  if (now - (lastReportedAt.get(key) || 0) < REPORT_THROTTLE_MS) {
    return false;
  }

  lastReportedAt.set(key, now);
  return true;
};

const reportNetworkError = (error) => {
  if (!isNetworkError(error) || navigator.onLine === false) {
    return;
  }

  const { method = 'get', url, baseURL } = error.config || {};
  const fullUrl = `${baseURL || ''}${url || ''}`;
  const endpoint = normalizeUrl(fullUrl);
  const key = `${method}:${endpoint}`;

  if (!shouldReport(key)) {
    return;
  }

  let host;
  try {
    host = new URL(fullUrl, window.location.origin).host;
  } catch (e) {
    host = 'unknown';
  }

  Sentry.captureException(error, {
    level: 'warning',
    fingerprint: ['network-error', host, method, endpoint],
    tags: {
      error_type: 'network_error',
      network_host: host,
      network_method: method.toUpperCase(),
      network_error_code: error.code || 'ERR_NETWORK'
    },
    extra: {
      endpoint,
      message: error.message,
      connectionType: navigator.connection?.effectiveType
    }
  });
};

const initNetworkErrorReporter = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      reportNetworkError(error);
      throw error;
    }
  );
};

export default initNetworkErrorReporter;
